import {
  Announcement,
  AnnouncementViewConfirmationRequest,
  TextPreset as BaseTextPreset,
  CaptionUserPreferences,
  CreateMyTextPresetArgs,
  CreatePersonalFontArgs,
  CreateProjectTemplateRequest,
  CreateTextPresetResponse,
  CreateWaveformPreferenceResponse,
  DeleteMyTextPresetArgs,
  DeleteProjectTemplateRequest,
  DeleteWaveformPreferenceRequest,
  DisplayPreferencesResponse,
  Experiment,
  GetMyExperimentVariantsRequest,
  GetOnboardingItemRequest,
  GetProjectTemplateCompatibilityRequest,
  GetUserDisplayPreferencesArgs,
  GetUSTCompatibilityRequest,
  GoogleFont,
  Language,
  NotificationAlert,
  OnboardingItem,
  OnboardingViewConfirmationRequest,
  PersonalFont,
  ProjectTemplate,
  ProjectTemplateCompatibility,
  ProjectTemplateUpdateRequest,
  Questionnaire,
  QuestionnaireStatus,
  SampleAudio,
  UpdatePersonalFontArgs,
  VideoExportUserPreferences,
  VideoExportUserPreferencesUpdateRequest,
  VideoSizeModel,
  WaveformUserPreferences,
} from '@sparemin/api-sdk';
import { CaptionsAnimationConfig } from '@sparemin/text-effects';
import { OnboardingName } from 'blocks/OnboardingAnimations/types';
import { DeepPartial, ISoundwave } from 'types';
import { AdvancedTextConfigs } from 'utils/text-templates/types';
import { IApiAction, IApiResponse } from '../types';

type ActionKey = 'HEADLINER_USER_SERVICE';
export const ACTION_KEY: ActionKey = 'HEADLINER_USER_SERVICE';

export enum ServiceMethod {
  GET_MY_FONTS = 'GET_MY_FONTS',
  GET_GOOGLE_FONTS = 'GET_GOOGLE_FONTS',
  POST_PERSONAL_FONTS = 'POST_PERSONAL_FONTS',
  UPDATE_FONT_NAME = 'UPDATE_FONT_NAME',
  DELETE_FONT = 'DELETE_FONT',
  GET_MY_DISPLAY_PREF = 'GET_MY_DISPLAY_PREF',
  PUT_MY_CAPTIONS_PREF = 'PUT_MY_CAPTIONS_PREF',
  GET_USER_DISPLAY_PREF = 'GET_USER_DISPLAY_PREF',
  GET_SAMPLE_AUDIOS = 'GET_SAMPLE_AUDIOS',
  GET_VIEW_CONFIRMATION = 'GET_VIEW_CONFIRMATION',
  GET_ANNOUNCMENTS = 'GET_ANNOUNCEMENTS',
  GET_ONBOARDING = 'GET_ONBOARDING',
  GET_ONBOARDING_VIEW_CONFIRMATION = 'GET_ONBOARDING_VIEW_CONFIRMATION',
  GET_PROJECT_TEMPLATES = 'GET_PROJECT_TEMPLATES',
  GET_TEXT_PRESETS = 'GET_TEXT_PRESETS',
  CREATE_TEXT_PRESET = 'CREATE_TEXT_PRESET',
  DELETE_TEXT_PRESET = 'DELETE_TEXT_PRESET',
  CREATE_PROJECT_TEMPLATE = 'CREATE_PROJECT_TEMPLATE',
  OVERWRITE_PROJECT_TEMPLATE = 'OVERWRITE_PROJECT_TEMPLATE',
  DELETE_PROJECT_TEMPLATE = 'DELETE_PROJECT_TEMPLATE',
  GET_PROJECT_TEMPLATE_COMPATIBILITY_FROM_CONFIGURATION_ID = 'GET_PROJECT_TEMPLATE_COMPATIBILITY_FROM_CONFIGURATION_ID',
  GET_USER_TEMPLATE_COMPATIBILITY = 'GET_USER_TEMPLATE_COMPATIBILITY',
  CREATE_QUESTIONNAIRE_RESPONSE = 'CREATE_QUESTIONNAIRE_RESPONSE',
  GET_QUESTIONNAIRE_RESPONSE = 'GET_QUESTIONNAIRE_RESPONSE',
  GET_QUESTIONNAIRE_STATUS = 'GET_QUESTIONNAIRE_STATUS',
  GET_MY_USER_PREF = 'GET_MY_USER_PREF',
  CREATE_WAVEFORM_PREF = 'CREATE_WAVEFORM_PREF',
  DELETE_WAVEFORM_PREF = 'DELETE_WAVEFORM_PREF',
  GET_EXPERIMENT_VARIANT = 'GET_EXPERIMENT_VARIANT',
  UPDATE_COLOR_PREF = 'UPDATE_COLOR_PREF',
  UPDATE_VIDEO_EXPORT_PREF = 'UPDATE_VIDEO_EXPORT_PREF',
  GET_PREF_VIDEOS_SIZES = 'GET_PREF_VIDEOS_SIZES',
  GET_EDDY_SUPPORTED_PROJECT_LANGUAGES = 'GET_EDDY_SUPPORTED_PROJECT_LANGUAGES',
  GET_NOTIFICATION_ALERT = 'GET_NOTIFICATION_ALERT',
  UPDATE_NOTIFICATION_ALERT = 'UPDATE_NOTIFICATION_ALERT',
  GET_AUTOMATION_SUPPORTED_LANGUAGES = 'GET_AUTOMATION_SUPPORTED_LANGUAGES',
  GET_CAPTION_SUPPORTED_LANGUAGES = 'GET_CAPTION_SUPPORTED_LANGUAGES',
}

export type UserId = number;

export type GetViewConfirmationArgs = Omit<
  AnnouncementViewConfirmationRequest,
  'userId'
>;

export type GetOnboardingArgs = GetOnboardingItemRequest<OnboardingName>;

export type GetOnboardingViewConfirmationArgs = Omit<
  OnboardingViewConfirmationRequest,
  'userId'
>;

export type GetQuestionnaireResponseArgs = undefined;

export type GetQuestionnaireStatusArgs = undefined;

export type CreateWaveformPrefArgs = WaveformUserPreferences<
  DeepPartial<ISoundwave>
>;

export type GetExperimentVariantArgs = Pick<
  GetMyExperimentVariantsRequest,
  'experimentName'
>;

export type UpdateColorPrefArgs = string[];

export type PutCaptionsPrefArgs = CaptionUserPreferences;

export type DeleteFontArgs = {
  fontId: string;
};

export interface IAudioGenre {
  id: number;
  name?: string;
}

export interface ISampleAudio {
  id: number;
  audioUrl: string;
  title: string;
  durationMillis: number;
  genre: IAudioGenre;
}

export interface INormalizedPersonalFonts {
  result: string[];
  entities: {
    personalFonts: {
      [id: string]: PersonalFont;
    };
  };
}

export interface UserPrefVideoDimensions {
  height: number;
  width: number;
}

export interface NormalizedVideoSizes {
  result: number[];
  entities: {
    videoSizes: {
      [id: number]: VideoSizeModel;
    };
  };
}

export interface NormalizedEddySupportedProjectLanguage {
  result: number[];
  entities: {
    eddySupportedProjectLanguages: {
      [id: number]: Language;
    };
  };
}

export interface NormalizedAutomationSupportedLanguage {
  result: number[];
  entities: {
    automationSupportedLanguages: {
      [id: number]: Language;
    };
  };
}

export interface NormalizedCaptionSupportedLanguage {
  result: number[];
  entities: {
    captionSupportedLanguages: {
      [id: number]: Language;
    };
  };
}

interface GoogleFonts {
  result: string[];
  entities: {
    googleFonts: {
      [id: string]: GoogleFont;
    };
  };
}

// export interface VideoExportPrefsArgs {
//   frameRate?: number;
//   fullEpisodeFrameRate?: number;
//   frameQuality?: number;
//   frameSize?: FrameSize;
//   fullEpisodeFrameSize?: FrameSize;
// }

interface INormalizedSampleAudios {
  result: string[];
  entities: {
    sampleAudios: {
      [id: string]: SampleAudio;
    };
  };
}

interface INormalizedAnnoucements {
  entities: {
    announcements: {
      [id: number]: Announcement;
    };
  };
  result: number;
}

export interface IAdjustmentConfig<T extends string, U> {
  valueType: T;
  default?: U;
  hide?: boolean;
  readonly?: boolean;
  overrideOptions?: U[];
  hideOptions?: U[];
  showOptions?: U[];
}

export type AudioSelectorMethod = 'upload' | 'podcastSearch';

export type LandscapeVideoExportDisplayPrefKey = '480p' | '720p' | '1080p';

export type PortraitVideoExportDisplayPrefKey =
  | 'Vertical Std'
  | 'Vertical HD'
  | 'Vertical HD2';

export type SquareVideoExportDisplayPrefKey =
  | 'Square Std'
  | 'Square HD'
  | 'Square HD2';

export type VideoExportDisplayPrefKey =
  | LandscapeVideoExportDisplayPrefKey
  | PortraitVideoExportDisplayPrefKey
  | SquareVideoExportDisplayPrefKey;

export interface ICaptionsPref {
  captionEnabled: boolean;
  captionLanguage: string;
}

export interface INormalizedDisplayPref {
  entities: {
    displayPref: { [key: string]: DisplayPreferencesResponse };
  };
  result: UserId;
}

export type IGetOnboarding = OnboardingItem<OnboardingName>;

interface INormalizedIProjectTemplates {
  entities: {
    projectTemplates: {
      [id: number]: ProjectTemplate;
    };
  };
  result: number;
}

export interface WaveformPref {
  id: number;
  dimensionWidth: number;
  dimensionHeight: number;
  waveformConfig: DeepPartial<ISoundwave>;
}

interface NormalizedUserPref {
  entities: {
    userPrefs: {
      [userId: number]: {
        captionPrefs: {
          captionEnabled: boolean;
          captionLanguage: string;
        };
        colorPrefs: string[];
        waveformPrefs: number[];
        videoExportPrefs: VideoExportUserPreferences;
        userId: number;
      };
    };
  };
  result: number;
}

export type GetPersonalFontsResult = INormalizedPersonalFonts;

export type GetGoogleFontsResult = GoogleFonts;

export type PostPersonalFontsResult = void;

export type UpdateFontNameResult = void;

export type DeleteFontResult = void;

export type GetDisplayPrefResult = DisplayPreferencesResponse;

export type GetPrefVideoSizesResult = NormalizedVideoSizes;

export type GetEddySupportedProjectLanguagesResult = NormalizedEddySupportedProjectLanguage;

export type GetAutomationSupportedLanguagesResult = NormalizedAutomationSupportedLanguage;

export type GetCaptionSupportedLanguagesResult = NormalizedCaptionSupportedLanguage;

export type PutCaptionsPrefResult = ICaptionsPref;

export type UpdateVideoExportPrefResult = void;

export type GetSampleAudiosResult = INormalizedSampleAudios;

export type GetAnnouncementsResult = INormalizedAnnoucements;

export type GetViewConfirmationResult = void;

export type GetOnboardingResult = IGetOnboarding;

export type GetOnboardingViewConfirmationResult = void;

export type GetProjectTemplatesResult = INormalizedIProjectTemplates;

export type CreateProjectTemplateResult = void;

export type OverwriteProjectTemplateResult = void;

export type DeleteProjectTemplateResult = void;

export type GetProjectTemplateCompatibilityFromConfigurationIdResult = {
  entities: {
    widgetTemplateCompatibility: Record<
      string,
      ProjectTemplateCompatibility & {
        widgetId: string;
      }
    >;
  };
  result: string;
};

export type CreateQuestionnaireResponseResult = void;

export type GetUserDisplayPrefResult = INormalizedDisplayPref;

export type GetMyUserPrefResult = NormalizedUserPref;

export type DeleteWaveformPrefResult = { waveformPrefId: number };

export type GetExperimentVariantResult = {
  entities: {
    experiments: Record<string, Experiment>;
  };
  result: string;
};

export type UpdateColorPrefResult = void;

export type TextPreset = BaseTextPreset<
  AdvancedTextConfigs,
  CaptionsAnimationConfig
>;

export interface GetTextPresetsResult {
  entities: {
    textPresets: Record<string, TextPreset>;
  };
  result: string;
}

export interface GetNotificationAlertResult {
  entities: NotificationAlert;

  result: boolean;
}

export type UpdateNotificationAlertResult = void;

export interface IHandle {
  (
    method: ServiceMethod.GET_MY_FONTS,
    args: undefined,
    token?: string,
  ): Promise<GetPersonalFontsResult>;
  (
    method: ServiceMethod.GET_GOOGLE_FONTS,
    args: undefined,
    token?: string,
  ): Promise<GetGoogleFontsResult>;
  (
    method: ServiceMethod.POST_PERSONAL_FONTS,
    args: CreatePersonalFontArgs,
    token?: string,
  ): Promise<PostPersonalFontsResult>;
  (
    method: ServiceMethod.UPDATE_FONT_NAME,
    args: UpdatePersonalFontArgs,
    token?: string,
  ): Promise<UpdateFontNameResult>;
  (
    method: ServiceMethod.DELETE_FONT,
    args: DeleteFontArgs,
    token?: string,
  ): Promise<DeleteFontResult>;
  (
    method: ServiceMethod.UPDATE_VIDEO_EXPORT_PREF,
    args: VideoExportUserPreferencesUpdateRequest,
    token?: string,
  ): Promise<UpdateColorPrefResult>;
  (
    method: ServiceMethod.GET_MY_DISPLAY_PREF,
    args: undefined,
    token?: string,
  ): Promise<GetDisplayPrefResult>;
  (
    method: ServiceMethod.GET_PREF_VIDEOS_SIZES,
    args: undefined,
    token?: string,
  ): Promise<GetPrefVideoSizesResult>;
  (
    method: ServiceMethod.GET_EDDY_SUPPORTED_PROJECT_LANGUAGES,
    args: undefined,
    token?: string,
  ): Promise<GetEddySupportedProjectLanguagesResult>;
  (
    method: ServiceMethod.GET_USER_DISPLAY_PREF,
    args: GetUserDisplayPreferencesArgs,
    token?: string,
  ): Promise<GetUserDisplayPrefResult>;
  (
    method: ServiceMethod.GET_SAMPLE_AUDIOS,
    args: undefined,
    token?: string,
  ): Promise<GetSampleAudiosResult>;

  (
    method: ServiceMethod.GET_ANNOUNCMENTS,
    args: undefined,
    token?: string,
  ): Promise<GetAnnouncementsResult>;

  (
    method: ServiceMethod.GET_VIEW_CONFIRMATION,
    args: GetViewConfirmationArgs,
    token?: string,
  ): Promise<GetViewConfirmationResult>;

  (
    method: ServiceMethod.GET_ONBOARDING,
    args: GetOnboardingArgs,
    token?: string,
  ): Promise<GetOnboardingResult>;

  (
    method: ServiceMethod.GET_ONBOARDING_VIEW_CONFIRMATION,
    args: GetOnboardingViewConfirmationArgs,
    token?: string,
  ): Promise<GetOnboardingViewConfirmationResult>;

  (
    method: ServiceMethod.GET_PROJECT_TEMPLATES,
    args: undefined,
    token?: string,
  ): Promise<GetProjectTemplatesResult>;

  (
    method: ServiceMethod.CREATE_PROJECT_TEMPLATE,
    args: CreateProjectTemplateRequest,
    token?: string,
  ): Promise<CreateProjectTemplateResult>;

  (
    method: ServiceMethod.DELETE_PROJECT_TEMPLATE,
    args: DeleteProjectTemplateRequest,
    token?: string,
  ): Promise<DeleteProjectTemplateResult>;

  (
    method: ServiceMethod.CREATE_QUESTIONNAIRE_RESPONSE,
    args: Questionnaire,
    token?: string,
  ): Promise<CreateQuestionnaireResponseResult>;

  (
    method: ServiceMethod.GET_QUESTIONNAIRE_RESPONSE,
    args: GetQuestionnaireResponseArgs,
    token?: string,
  ): Promise<Questionnaire>;

  (
    method: ServiceMethod.GET_QUESTIONNAIRE_STATUS,
    args: GetQuestionnaireStatusArgs,
    token?: string,
  ): Promise<QuestionnaireStatus>;

  (
    method: ServiceMethod.GET_MY_USER_PREF,
    args: undefined,
    token?: string,
  ): Promise<GetMyUserPrefResult>;

  (
    method: ServiceMethod.CREATE_WAVEFORM_PREF,
    args: CreateWaveformPrefArgs,
    token?: string,
  ): Promise<CreateWaveformPreferenceResponse>;

  (
    method: ServiceMethod.DELETE_WAVEFORM_PREF,
    args: DeleteWaveformPreferenceRequest,
    token?: string,
  ): Promise<DeleteWaveformPrefResult>;

  (
    method: ServiceMethod.GET_EXPERIMENT_VARIANT,
    args: GetExperimentVariantArgs,
    token?: string,
  ): Promise<GetExperimentVariantResult>;

  (
    method: ServiceMethod.UPDATE_COLOR_PREF,
    args: UpdateColorPrefArgs,
    token?: string,
  ): Promise<UpdateColorPrefResult>;
  (
    method: ServiceMethod.GET_NOTIFICATION_ALERT,
    args: undefined,
    token?: string,
  ): Promise<GetNotificationAlertResult>;
  (
    method: ServiceMethod.UPDATE_NOTIFICATION_ALERT,
    args: NotificationAlert,
    token?: string,
  ): Promise<UpdateNotificationAlertResult>;
  (
    method: ServiceMethod.GET_AUTOMATION_SUPPORTED_LANGUAGES,
    args: undefined,
    token?: string,
  ): Promise<GetAutomationSupportedLanguagesResult>;
  (
    method: ServiceMethod.GET_CAPTION_SUPPORTED_LANGUAGES,
    args: undefined,
    token?: string,
  ): Promise<GetCaptionSupportedLanguagesResult>;
}

type ApiAction<M extends ServiceMethod, A> = IApiAction<ActionKey, M, A>;
export type GetMyFontsAction = ApiAction<ServiceMethod.GET_MY_FONTS, undefined>;

export type GetGoogleFontsAction = ApiAction<
  ServiceMethod.GET_GOOGLE_FONTS,
  undefined
>;

export type PostPersonalFontsAction = ApiAction<
  ServiceMethod.POST_PERSONAL_FONTS,
  CreatePersonalFontArgs
>;

export type UpdateFontNameAction = ApiAction<
  ServiceMethod.UPDATE_FONT_NAME,
  UpdatePersonalFontArgs
>;

export type DeleteFontAction = ApiAction<
  ServiceMethod.DELETE_FONT,
  DeleteFontArgs
>;

export type GetMyDisplayPrefAction = ApiAction<
  ServiceMethod.GET_MY_DISPLAY_PREF,
  undefined
>;
export type GetMyDisplayPrefResult = GetDisplayPrefResult;

export type PutMyCaptionsPrefAction = ApiAction<
  ServiceMethod.PUT_MY_CAPTIONS_PREF,
  PutCaptionsPrefArgs
>;
export type PutMyCaptionsPrefResult = PutCaptionsPrefResult;

export type GetUserDisplayPrefAction = ApiAction<
  ServiceMethod.GET_USER_DISPLAY_PREF,
  GetUserDisplayPreferencesArgs
>;

export type GetSampleAudiosAction = ApiAction<
  ServiceMethod.GET_SAMPLE_AUDIOS,
  undefined
>;
export type GetAnnouncementsAction = ApiAction<
  ServiceMethod.GET_ANNOUNCMENTS,
  undefined
>;
export type GetViewConfirmationAction = ApiAction<
  ServiceMethod.GET_VIEW_CONFIRMATION,
  GetViewConfirmationArgs
>;

export type GetOnboardingAction = ApiAction<
  ServiceMethod.GET_ONBOARDING,
  GetOnboardingArgs
>;

export type GetOnboardingViewConfirmationAction = ApiAction<
  ServiceMethod.GET_ONBOARDING_VIEW_CONFIRMATION,
  GetOnboardingViewConfirmationArgs
>;

export type GetProjectTemplatesAction = ApiAction<
  ServiceMethod.GET_PROJECT_TEMPLATES,
  undefined
>;

export type CreateProjectTemplateAction = ApiAction<
  ServiceMethod.CREATE_PROJECT_TEMPLATE,
  CreateProjectTemplateRequest
>;

export type OverwriteProjectTemplateAction = ApiAction<
  ServiceMethod.OVERWRITE_PROJECT_TEMPLATE,
  ProjectTemplateUpdateRequest
>;

export type DeleteProjectTemplateAction = ApiAction<
  ServiceMethod.DELETE_PROJECT_TEMPLATE,
  DeleteProjectTemplateRequest
>;

export type GetProjectTemplateCompatibilityFromConfigurationIdAction = ApiAction<
  ServiceMethod.GET_PROJECT_TEMPLATE_COMPATIBILITY_FROM_CONFIGURATION_ID,
  GetProjectTemplateCompatibilityRequest
>;

export type GetUserTemplateCompatibilityAction = ApiAction<
  ServiceMethod.GET_USER_TEMPLATE_COMPATIBILITY,
  GetUSTCompatibilityRequest
>;

export type CreateQuestionnaireResponseAction = ApiAction<
  ServiceMethod.CREATE_QUESTIONNAIRE_RESPONSE,
  Questionnaire
>;

export type GetQuestionnaireResponseAction = ApiAction<
  ServiceMethod.GET_QUESTIONNAIRE_RESPONSE,
  GetQuestionnaireResponseArgs
>;

export type GetQuestionnaireStatusAction = ApiAction<
  ServiceMethod.GET_QUESTIONNAIRE_STATUS,
  GetQuestionnaireStatusArgs
>;

export type GetMyUserPrefAction = ApiAction<
  ServiceMethod.GET_MY_USER_PREF,
  undefined
>;

export type CreateWaveformPrefAction = ApiAction<
  ServiceMethod.CREATE_WAVEFORM_PREF,
  CreateWaveformPrefArgs
>;

export type DeleteWaveformPrefAction = ApiAction<
  ServiceMethod.DELETE_WAVEFORM_PREF,
  DeleteWaveformPreferenceRequest
>;

export type GetExperimentVariantAction = ApiAction<
  ServiceMethod.GET_EXPERIMENT_VARIANT,
  GetExperimentVariantArgs
>;

export type UpdateColorPrefAction = ApiAction<
  ServiceMethod.UPDATE_COLOR_PREF,
  UpdateColorPrefArgs
>;

export type UpdateVideoExportPrefAction = ApiAction<
  ServiceMethod.UPDATE_VIDEO_EXPORT_PREF,
  VideoExportUserPreferencesUpdateRequest
>;

export type GetPrefVideoSizesAction = ApiAction<
  ServiceMethod.GET_PREF_VIDEOS_SIZES,
  undefined
>;

export type GetEddySupportedProjectLanguagesAction = ApiAction<
  ServiceMethod.GET_EDDY_SUPPORTED_PROJECT_LANGUAGES,
  undefined
>;

export type GetTextPresetsAction = ApiAction<
  ServiceMethod.GET_TEXT_PRESETS,
  GetTextPresetsResult
>;

export type CreateTextPresetAction = ApiAction<
  ServiceMethod.CREATE_TEXT_PRESET,
  CreateMyTextPresetArgs
>;

export type DeleteTextPresetAction = ApiAction<
  ServiceMethod.DELETE_TEXT_PRESET,
  DeleteMyTextPresetArgs
>;

export type GetNotificationAlertAction = ApiAction<
  ServiceMethod.GET_NOTIFICATION_ALERT,
  undefined
>;

export type UpdateNotificationAlertAction = ApiAction<
  ServiceMethod.UPDATE_NOTIFICATION_ALERT,
  NotificationAlert
>;

export type GetAutomationSupportedLanguagesAction = ApiAction<
  ServiceMethod.GET_AUTOMATION_SUPPORTED_LANGUAGES,
  undefined
>;

export type GetCaptionSupportedLanguagesAction = ApiAction<
  ServiceMethod.GET_CAPTION_SUPPORTED_LANGUAGES,
  undefined
>;

export interface HeadlinerUserServiceDispatch {
  (action: GetMyFontsAction): Promise<IApiResponse<GetPersonalFontsResult>>;
  (action: GetGoogleFontsAction): Promise<IApiResponse<GetGoogleFontsResult>>;
  (action: GetMyDisplayPrefAction): Promise<IApiResponse<GetDisplayPrefResult>>;
  (action: PutMyCaptionsPrefAction): Promise<
    IApiResponse<PutCaptionsPrefResult>
  >;
  (action: GetUserDisplayPrefAction): Promise<
    IApiResponse<GetUserDisplayPrefResult>
  >;

  (action: GetSampleAudiosAction): Promise<IApiResponse<GetSampleAudiosResult>>;
  (action: GetAnnouncementsAction): Promise<
    IApiResponse<GetAnnouncementsResult>
  >;
  (action: GetViewConfirmationAction): Promise<
    IApiResponse<GetViewConfirmationResult>
  >;
  (action: GetOnboardingAction): Promise<IApiResponse<GetOnboardingResult>>;
  (action: GetOnboardingViewConfirmationAction): Promise<
    IApiResponse<GetOnboardingViewConfirmationResult>
  >;
  (action: GetProjectTemplatesAction): Promise<
    IApiResponse<GetProjectTemplatesResult>
  >;
  (action: CreateProjectTemplateAction): Promise<
    IApiResponse<CreateProjectTemplateResult>
  >;
  (action: OverwriteProjectTemplateAction): Promise<
    IApiResponse<OverwriteProjectTemplateResult>
  >;
  (action: DeleteProjectTemplateAction): Promise<
    IApiResponse<DeleteProjectTemplateResult>
  >;
  (action: GetProjectTemplatesAction): Promise<
    IApiResponse<GetProjectTemplatesResult>
  >;
  (action: CreateQuestionnaireResponseAction): Promise<
    IApiResponse<CreateQuestionnaireResponseResult>
  >;
  (action: GetQuestionnaireResponseAction): Promise<
    IApiResponse<Questionnaire>
  >;
  (action: GetQuestionnaireStatusAction): Promise<
    IApiResponse<QuestionnaireStatus>
  >;
  (action: GetMyUserPrefAction): Promise<IApiResponse<GetMyUserPrefResult>>;
  (action: CreateWaveformPrefAction): Promise<
    IApiResponse<CreateWaveformPreferenceResponse>
  >;
  (action: DeleteWaveformPrefAction): Promise<
    IApiResponse<DeleteWaveformPrefResult>
  >;
  (action: GetExperimentVariantAction): Promise<
    IApiResponse<GetExperimentVariantResult>
  >;
  (action: UpdateColorPrefAction): Promise<IApiResponse<UpdateColorPrefResult>>;
  (action: UpdateVideoExportPrefAction): Promise<
    IApiResponse<UpdateVideoExportPrefResult>
  >;
  (action: GetPrefVideoSizesAction): Promise<
    IApiResponse<GetPrefVideoSizesResult>
  >;
  (action: GetEddySupportedProjectLanguagesAction): Promise<
    IApiResponse<GetEddySupportedProjectLanguagesResult>
  >;
  (action: GetTextPresetsAction): Promise<IApiResponse<GetTextPresetsResult>>;
  (action: CreateTextPresetAction): Promise<
    IApiResponse<CreateTextPresetResponse>
  >;
  (action: DeleteTextPresetAction): Promise<IApiResponse<void>>;
  (action: GetNotificationAlertAction): Promise<
    IApiResponse<GetNotificationAlertResult>
  >;
  (action: UpdateNotificationAlertAction): Promise<
    IApiResponse<UpdateNotificationAlertResult>
  >;
  (action: GetAutomationSupportedLanguagesAction): Promise<
    IApiResponse<GetAutomationSupportedLanguagesResult>
  >;
  (action: GetCaptionSupportedLanguagesAction): Promise<
    IApiResponse<GetCaptionSupportedLanguagesResult>
  >;
  (action: GetProjectTemplateCompatibilityFromConfigurationIdAction): Promise<
    IApiResponse<GetProjectTemplateCompatibilityFromConfigurationIdResult>
  >;
  (action: GetUserTemplateCompatibilityAction): Promise<
    IApiResponse<ProjectTemplateCompatibility>
  >;
}
